import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Play, Video } from "lucide-react";
import { InteractiveMaterialResponseDto } from "@packages/api/madlenAPI.schemas";
import { Text, Button } from "@core/index";
import {
  VideoSection,
  ContentSection,
  EditButtons,
  SaveCancelButtons,
  useEditState,
  useVideoSection,
  useTimeInput,
  formatTimestamp,
  convertSecondsToTime,
} from "./utils";

interface GlossaryItem {
  term: string;
  definition: string;
  startTime: number;
  duration: number;
}

interface GlossaryTabProps {
  material: InteractiveMaterialResponseDto;
  onGlossaryChange?: (glossaries: GlossaryItem[]) => void;
}

const GlossaryTab = ({ material, onGlossaryChange }: GlossaryTabProps) => {
  const { t } = useTranslation();
  const glossaryListRef = useRef<HTMLDivElement>(null);
  const [glossaryItems, setGlossaryItems] = useState<GlossaryItem[]>(
    (material.glossaries as GlossaryItem[]) || [],
  );

  const videoSection = useVideoSection();
  const timeInput = useTimeInput({
    onTimeChange: (seconds) => {
      if (editingItem) {
        setEditingItem({
          ...editingItem,
          startTime: seconds,
        });
      }
    },
  });

  const {
    editingIndex,
    editingItem,
    setEditingItem,
    handleEdit: baseHandleEdit,
    handleDelete,
    handleSave: baseHandleSave,
    handleCancel: baseHandleCancel,
    handleCreate: baseHandleCreate,
  } = useEditState({
    items: glossaryItems,
    onItemsChange: onGlossaryChange,
    createNewItem: () => ({
      term: "",
      definition: "",
      startTime: 0,
      duration: 0,
    }),
    setItems: setGlossaryItems,
    containerRef: glossaryListRef,
  });

  const handleEdit = (index: number) => {
    baseHandleEdit(index);
    const item = glossaryItems[index];
    timeInput.setTimeInputValue(convertSecondsToTime(item.startTime));
  };

  const handleSave = (index: number) => {
    baseHandleSave(index);
    timeInput.setTimeInputValue("");
  };

  const handleCancel = () => {
    baseHandleCancel();
    timeInput.setTimeInputValue("");
  };

  const handleCreate = () => {
    baseHandleCreate();
    timeInput.setTimeInputValue("0:00");
  };

  useEffect(() => {
    setGlossaryItems((material.glossaries as GlossaryItem[]) || []);
  }, [material.glossaries]);

  return (
    <div className="h-[calc(100vh-20rem)] max-h-[800px] min-h-[600px] flex flex-col lg:flex-row gap-4 relative">
      <VideoSection
        {...videoSection}
        youtubeUrl={material.youtubeUrl}
        generatorMode={material.generatorMode}
      />

      <ContentSection
        title={t("interactive_material.glossary.title")}
        onAddNew={handleCreate}
        addButtonLabel={t("interactive_material.glossary.addNew")}
        isEmpty={glossaryItems.length === 0}
        emptyStateMessage={t("interactive_material.glossary.empty")}
        containerRef={glossaryListRef}
      >
        {glossaryItems.map((item, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200/50 dark:border-gray-700/50 transition-all hover:shadow-md"
          >
            {editingIndex === index && editingItem ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.glossary.term")}
                  </label>
                  <input
                    type="text"
                    value={editingItem.term}
                    onChange={(e) =>
                      setEditingItem({ ...editingItem, term: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors"
                    placeholder={t(
                      "interactive_material.glossary.termPlaceholder",
                    )}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("interactive_material.glossary.definition")}
                  </label>
                  <textarea
                    value={editingItem.definition}
                    onChange={(e) =>
                      setEditingItem({
                        ...editingItem,
                        definition: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-colors resize-none"
                    rows={3}
                    placeholder={t(
                      "interactive_material.glossary.definitionPlaceholder",
                    )}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div className="space-y-2 flex-1 w-full">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Text variant={"h3"} className="break-all">
                      {item.term}
                    </Text>
                  </div>
                  <Text
                    variant="p"
                    status="subtle"
                    size={"base"}
                    className="break-words"
                  >
                    {item.definition}
                  </Text>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                  <EditButtons
                    onEdit={() => handleEdit(index)}
                    onDelete={() => handleDelete(index)}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </ContentSection>
    </div>
  );
};

export default GlossaryTab;
